import React from 'react';
import {
    getReportEntityFlag,
    getReportEntityLocationLabel,
    isRestrictedArea,
} from 'core/services/report-entities-service/report-entities-service';
import { BillboardPOI, ChainPOI, MarketPOI, PlacePOI, TagPOI, VenuePOI } from 'core/entities';
import { venueFlagInfoPublicPOIText } from 'ui-components/venue-status-indicator/v2/venue-status-indicator-v2';
import {
    AlertColoredIcon,
    AlertOrangeIcon,
    AlertRedIcon,
    PinIcon,
    RecentIcon,
    CustomVenueIcon,
    TagIcon,
    VenueIcon,
} from 'components/assets/Icons/Icons';
import { isRecentEntity } from 'core/entities/recent-entity/recent-entity';
import { isCustomEntity } from 'core/entities/custom-entity/custom-entity';
import { GrayLockIcon } from 'components/assets/Icons/GrayLockIcon';
import globalStyles from 'src/react.module.scss';
import { getSimpleFormatDate, dateFormats } from 'utils/date/date';
import { Complex } from '@placer-ui/types';
import { BillboardIcon, ChainIcon, ZipCodeIcon } from '@placer-ui/components';
import { WarningInfo } from 'features/header/header-top-panel/search-section/components/search-autocomplete/auto-complete-utils/types';

type ExtractPoiRelevantPropertiesParams = {
    poi: Complex;
    hasUnlockNearbyActivity?: boolean;
    hasRestrictPermission?: boolean;
    hasNearbyPropertyVenueIndicatorFF?: boolean;
    flaggedEntityTooltipText?: string;
};

export const extractPoiRelevantProperties = ({
    poi,
    hasUnlockNearbyActivity,
    hasRestrictPermission,
    hasNearbyPropertyVenueIndicatorFF,
    flaggedEntityTooltipText,
}: ExtractPoiRelevantPropertiesParams) => {
    const location = getReportEntityLocationLabel(poi);
    const { name: poiName, id: poiId } = poi;
    const poiFlagType = getReportEntityFlag(poi);
    const isPrivacyFlag = poiFlagType === 'privacy_concerns';
    const isRestricted = hasRestrictPermission && isRestrictedArea(poi);
    const isProviderPresent = !!poi.provider_data?.provider;
    const isNearbyProfile = PlacePOI.isNearbyActivity(poi);
    const flagsToIgnore = ['indoor', 'multi_story'];
    const noUnlockNearbyActivity =
        hasUnlockNearbyActivity === false && !!poiFlagType && !flagsToIgnore.includes(poiFlagType);
    const storeId = poi?.store_id;
    const flagInfo = poiFlagType ? venueFlagInfoPublicPOIText[poiFlagType] : '';

    let flaggedTooltipText;

    if (isRestricted) {
        flaggedTooltipText = 'Venue is part of a restricted area. Data is not available';
    } else if (isNearbyProfile && hasNearbyPropertyVenueIndicatorFF) {
        flaggedTooltipText = `${poiName} only supports a Nearby Activity report`;
    } else if (noUnlockNearbyActivity) {
        flaggedTooltipText = 'Locked. Premium account is needed';
    } else if (!flaggedEntityTooltipText) {
        flaggedTooltipText = `${flagInfo} Click to view other report options.`;
    } else {
        flaggedTooltipText = flaggedEntityTooltipText;
    }

    return {
        location,
        poiName,
        poiId,
        poiFlagType,
        isPrivacyFlag,
        isRestricted,
        isProviderPresent,
        isNearbyProfile,
        noUnlockNearbyActivity,
        storeId,
        flaggedTooltipText,
    };
};

export const getPoiWarningInfo = ({
    hasAnyWarning,
    isPrivacyFlag,
    isRestricted,
    isNearbyProfile,
    hasNearbyPropertyVenueIndicatorFF,
    noUnlockNearbyActivity,
    flaggedTooltipText,
    closingDate,
}: {
    hasAnyWarning: boolean;
    isPrivacyFlag?: boolean;
    isRestricted?: boolean;
    isNearbyProfile?: boolean;
    hasNearbyPropertyVenueIndicatorFF?: boolean;
    noUnlockNearbyActivity?: boolean;
    flaggedTooltipText?: string;
    closingDate?: string | null;
}): WarningInfo | undefined => {
    if (hasAnyWarning) {
        const alertBlackIcon = ({ ...props }) =>
            AlertColoredIcon({ style: { color: 'var(--text-primary-color)' }, ...props });
        const alertPurpleIcon = ({ ...props }) =>
            AlertColoredIcon({ style: { color: globalStyles.lightBlue }, ...props });

        let warningFlagIcon = AlertOrangeIcon;
        if (isPrivacyFlag || isRestricted) {
            warningFlagIcon = alertBlackIcon;
        } else if (isNearbyProfile && hasNearbyPropertyVenueIndicatorFF) {
            warningFlagIcon = alertPurpleIcon;
        }

        const WarningIcon = noUnlockNearbyActivity ? GrayLockIcon : warningFlagIcon;

        return {
            Icon: WarningIcon,
            tooltipText: flaggedTooltipText || '',
        };
    } else if (closingDate) {
        return {
            Icon: AlertRedIcon,
            tooltipText: `Closed - Date available until ${getSimpleFormatDate(
                closingDate,
                dateFormats.shortMonthYearWithNumericOrdinalContractionDayComma,
            )}`,
        };
    }
    return undefined;
};

export const getPoiIcon = (poi: Complex) => {
    const { provider_data: providerData } = poi;
    const { provider: isProviderPresent, entity_type: providerEntityType } = providerData || {};

    if (MarketPOI.isMarketPOI(poi)) return <ZipCodeIcon />;
    if (TagPOI.isTag(poi)) return <TagIcon />;
    if (BillboardPOI.isBillboard(poi)) return isProviderPresent ? <PinIcon /> : <BillboardIcon />;
    if (VenuePOI.isVenue(poi)) return isProviderPresent ? <PinIcon /> : <VenueIcon />;
    if (ChainPOI.isChain(poi)) return <ChainIcon />;
    if (isCustomEntity(poi)) return isProviderPresent ? <PinIcon /> : <CustomVenueIcon />;
    if (isRecentEntity(poi)) return isProviderPresent ? <PinIcon /> : <RecentIcon />;
    if (isProviderPresent && providerEntityType === 'address') return <PinIcon />;

    return undefined;
};

export const isAddressProviderType = (poi: Complex) => {
    const { provider_data: providerData } = poi;
    const { entity_type: providerEntityType } = providerData || {};
    return providerEntityType === 'address';
};
